import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { createRefetchContainer, graphql } from 'react-relay';
import scrollToComponent from 'react-scroll-to-component';
import { Redirect } from 'react-router-dom';

import EventOnlineView from '../components/content/event/EventOnlineView';
import Faq from '../components/content/Faq';
import InvalidFaceFileInformation from '../components/content/informations/InvalidFaceFileInformation';
import LoadingView from '../components/content/LoadingView';
import FaceMediaList from '../components/content/lists/FaceMediaList';
import withRequestHandler from '../components/hoc/withRequestHandler';

class EventFaceSearchPage extends Component {
  constructor(props) {
    super(props);

    this.state = {
      extendedSearchSelectedTab: 3,
      extendedSearchExpand: true,
      showInvalidFaceFileInformation: false,
    };
  }

  componentDidUpdate(prevProps) {
    const { faceStartnumberFromUrl } = this.props;
    if (faceStartnumberFromUrl !== prevProps.faceStartnumberFromUrl) {
      this._refetchMediaForNewFace(faceStartnumberFromUrl);
    }
  }

  handleExtendedSearchTabChanged = (event, value) => {
    this.setState({
      extendedSearchSelectedTab: value,
      extendedSearchExpand: true,
    });
  };

  handleExtendedSearchExpand = () => {
    this.setState(prevState => {
      const {
        extendedSearchExpand: prevExpand,
        extendedSearchSelectedTab: prevSelectedTab,
      } = prevState;

      return {
        extendedSearchExpand: !prevExpand,
        // if the extended search changes from open to close it is important to reset
        // the selected tab to its default. Otherwise the media list will not be rendered.
        extendedSearchSelectedTab: prevExpand ? 3 : prevSelectedTab,
      };
    });
  };

  handleDeepSearchBtnClicked = () => {
    this.scrollToEventOnlineView(0);
  };

  handleCategorySearchBtnClicked = () => {
    this.scrollToEventOnlineView(1);
  };

  handleGpsSearchBtnClicked = () => {
    this.scrollToEventOnlineView(2);
  };

  handleFaceSearchBtnClicked = () => {
    this.scrollToEventOnlineView(3);
  };

  scrollToEventOnlineView(tabIndex) {
    scrollToComponent(this.eventOnlineView);
    this.setState({
      extendedSearchExpand: true,
      extendedSearchSelectedTab: tabIndex,
    });
  }

  _refetchMediaForNewFace = newFaceStartnumber => {
    this.props.onRequestStart();
    this.setState({ showInvalidFaceFileInformation: false });
    this.props.relay.refetch({ faceStartnumber: newFaceStartnumber }, null, err => {
      if (err) {
        this.props.onRequestStop();
        this.setState({ showInvalidFaceFileInformation: true });
      } else {
        this.props.onRequestStop();
      }
    });
  };

  render() {
    const {
      eventHasSecondFactor,
      isLoading,
      snFromUrl,
      faceStartnumberFromUrl,
      viewer,
      customerKey,
    } = this.props;
    const { event } = viewer;

    if (event.state === 'reminder') {
      return <Redirect to={`/event/${event.sgId}`} />;
    }

    const participantMediaCount = event.participant
      ? event.participant.media.mediaInfo.count
      : 0;

    const {
      extendedSearchSelectedTab,
      extendedSearchExpand,
      showInvalidFaceFileInformation,
    } = this.state;

    return (
      <div className="container-960 container-page">
        <EventOnlineView
          ref={view => (this.eventOnlineView = view)}
          onlineEvent={event}
          urlSn={snFromUrl}
          customerKey={customerKey}
          extendedSearchSelectedTab={extendedSearchSelectedTab}
          extendedSearchExpand={extendedSearchExpand}
          hasSmartSearchTab={event.smartSearch && participantMediaCount !== 0}
          hasGpsSearchTab={event.gps}
          hasFaceSearchTab={event.searchByFaceAsFeature && (!event.searchByFace || participantMediaCount !== 0)}
          onExtendedSearchTabChange={this.handleExtendedSearchTabChanged}
          onExtendedSearchExpand={this.handleExtendedSearchExpand}
        />
        <hr className="col-xs-16 col-sm-12" />
        {isLoading && <LoadingView />}
        {!isLoading &&
          !showInvalidFaceFileInformation &&
          event.searchMediaByFace.mediaInfo.count !== 0 &&
          extendedSearchSelectedTab === 3 && (
            <FaceMediaList
              eventHasSearchByFace={event.searchByFace ? true : false}
              eventHasSecondFactor={eventHasSecondFactor}
              eventId={event.sgId}
	      startnumber={snFromUrl}
              faceStartnumber={faceStartnumberFromUrl}
              faceMedia={event.searchMediaByFace}
              offer={event}
              participantMediaCount={participantMediaCount}
            />
          )}
        {!isLoading &&
          (showInvalidFaceFileInformation ||
            event.searchMediaByFace.mediaInfo.count === 0) &&
          extendedSearchSelectedTab === 3 && <InvalidFaceFileInformation />}
        <Faq onFindMore={() => this.scrollToEventOnlineView(0)} />
      </div>
    );
  }
}

EventFaceSearchPage.propTypes = {
  customerKey: PropTypes.string,
  eventHasSearchByFace: PropTypes.bool,
  eventHasSecondFactor: PropTypes.bool,
  isLoading: PropTypes.bool,
  snFromUrl: PropTypes.string,
  faceStartnumberFromUrl: PropTypes.string,
  viewer: PropTypes.object,
  relay: PropTypes.object,
  onRequestStart: PropTypes.func,
  onRequestStop: PropTypes.func,
};

export { EventFaceSearchPage };

export default createRefetchContainer(
  withRequestHandler(EventFaceSearchPage),
  {
    viewer: graphql`
      fragment EventFaceSearchPage_viewer on Viewer {
        event(id: $eventId, language: $language) {
          id
          sgId
          gps
          hostResultUrl

          participant(startnumber: $startnumber, secondFactor: $secondFactor) @skip(if: $noStartnumber){
            media {
              mediaInfo {
                count
              }
            }
            startnumber
          }
          searchMediaByFace(startnumber: $startnumber, face: $faceStartnumber) {
            mediaInfo {
              count
            }
            ...FaceMediaList_faceMedia
          }
          secondFactorType {
            type
          }
          searchByFace
          searchByFaceAsFeature
          smartSearch
          state
          ...EventOnlineView_onlineEvent
          ...FaceMediaList_offer
        }
      }
    `,
  },
  graphql`
    query EventFaceSearchPageRefetchQuery(
      $eventId: ID!
      $language: String
      $startnumber: ID!
      $faceStartnumber: ID!
      $count: Int!
      $cursor: String
      $secondFactor: String
      $individualPart: String
      $noStartnumber: Boolean
    ) {
      viewer {
        ...EventFaceSearchPage_viewer
      }
    }
  `
);
